import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Box,
  Collapse,
  Chip,
  CircularProgress,
  Alert,
  useTheme,
  Tooltip,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CloseIcon from '@mui/icons-material/Close';
import { getImportHistory, downloadImportFile } from '../services/importService';
import { tokens } from '../theme';

const ImportHistoryDialog = ({ open, onClose }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    if (open) {
      fetchHistory();
    }
  }, [open]);

  const fetchHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getImportHistory();
      setHistory(data.history || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors du chargement de l\'historique');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (historyId) => {
    setDownloading(historyId);
    try {
      await downloadImportFile(historyId);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors du téléchargement du fichier');
    } finally {
      setDownloading(null);
    }
  };

  const handleToggleRow = (rowId) => {
    setExpandedRow(expandedRow === rowId ? null : rowId);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: '500px',
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontWeight: 600,
          fontSize: 20,
          borderBottom: `1px solid ${colors.grey[300]}`,
          pb: 2,
        }}
      >
        Historique des importations
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : history.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="body1" color="text.secondary">
              Aucun historique d'importation disponible
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none', border: `1px solid ${colors.grey[300]}` }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: colors.grey[100] }}>
                  <TableCell sx={{ fontWeight: 600, width: 50 }}></TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Fichier</TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Total</TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Importés</TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Ignorés</TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Erreurs</TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((item) => (
                  <>
                    <TableRow key={item.id} hover>
                      <TableCell>
                        <IconButton size="small" onClick={() => handleToggleRow(item.id)}>
                          {expandedRow === item.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(item.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {item.filename}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Chip
                          label={item.total_rows}
                          size="small"
                          sx={{ bgcolor: colors.grey[200], fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Chip
                          label={item.success_count}
                          size="small"
                          sx={{ bgcolor: '#d4edda', color: '#155724', fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Chip
                          label={item.skipped_count}
                          size="small"
                          sx={{ bgcolor: '#fff3cd', color: '#856404', fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Chip
                          label={item.error_count}
                          size="small"
                          sx={{
                            bgcolor: item.error_count > 0 ? '#f8d7da' : colors.grey[200],
                            color: item.error_count > 0 ? '#721c24' : 'inherit',
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Tooltip title="Télécharger le fichier CSV">
                          <IconButton
                            size="small"
                            onClick={() => handleDownload(item.id)}
                            disabled={downloading === item.id}
                            sx={{
                              color: colors.primary[500],
                              '&:hover': {
                                backgroundColor: colors.primary[100],
                              }
                            }}
                          >
                            {downloading === item.id ? (
                              <CircularProgress size={20} />
                            ) : (
                              <DownloadIcon />
                            )}
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                        <Collapse in={expandedRow === item.id} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 2, px: 3, backgroundColor: colors.grey[50] }}>
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                              Mappage des champs utilisé :
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                              {Object.entries(item.field_mapping || {}).map(([csvCol, profileField]) => (
                                profileField !== 'skip' && (
                                  <Chip
                                    key={csvCol}
                                    label={`${csvCol} → ${profileField}`}
                                    size="small"
                                    sx={{
                                      bgcolor: colors.primary[100],
                                      color: colors.primary[700],
                                    }}
                                  />
                                )
                              ))}
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: `1px solid ${colors.grey[300]}` }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            px: 4,
            py: 1,
            fontWeight: 600,
            backgroundColor: colors.primary[500],
            '&:hover': {
              backgroundColor: colors.primary[600],
            }
          }}
        >
          Fermer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportHistoryDialog;

